import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { supabase as supabaseClient } from "@/integrations/supabase/client";
import { toast } from "@/hooks/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Database } from "@/integrations/supabase/types";
import { Clock, AlertTriangle, CheckCircle } from "lucide-react";
import { SettingsCard } from './ui/SettingsCard';

// Define interface for daily opening hours
interface DailyOpeningHours {
  day: string; // Use string for day name
  openingTime: string; // HH:mm format
  closingTime: string; // HH:mm format
  isClosedAllDay: boolean; // Added to indicate if the business is closed all day
  is24Hours: boolean; // Added to indicate if the business is open 24 hours
}

interface BusinessOpeningHoursManagerProps {
  businessId: string | null;
}

const BusinessOpeningHoursManager: React.FC<BusinessOpeningHoursManagerProps> = ({ businessId }) => {
  const [dailyOpeningHours, setDailyOpeningHours] = useState<DailyOpeningHours[]>([]);
  const [loadingOpeningHours, setLoadingOpeningHours] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ [key: number]: { openingTime?: boolean; closingTime?: boolean; timeOrder?: boolean } }>({});
  const [isEditing, setIsEditing] = useState(false);
  const [originalHours, setOriginalHours] = useState<DailyOpeningHours[]>([]);


  // Generate hour options (00-23)
  const hourOptions = useMemo(() => {
    const options = [];
    for (let h = 0; h < 24; h++) {
      options.push(h.toString().padStart(2, '0'));
    }
    return options;
  }, []);

  // Generate minute options (00, 15, 30, 45)
  const minuteOptions = useMemo(() => ['00', '15', '30', '45'], []);

  // Default opening hours for initialization
  const defaultOpeningHours = [
    { day: 'Monday', openingTime: '09:00', closingTime: '17:00', isClosedAllDay: false, is24Hours: false },
    { day: 'Tuesday', openingTime: '09:00', closingTime: '17:00', isClosedAllDay: false, is24Hours: false },
    { day: 'Wednesday', openingTime: '09:00', closingTime: '17:00', isClosedAllDay: false, is24Hours: false },
    { day: 'Thursday', openingTime: '09:00', closingTime: '17:00', isClosedAllDay: false, is24Hours: false },
    { day: 'Friday', openingTime: '09:00', closingTime: '17:00', isClosedAllDay: false, is24Hours: false },
    { day: 'Saturday', openingTime: '', closingTime: '', isClosedAllDay: true, is24Hours: false },
    { day: 'Sunday', openingTime: '', closingTime: '', isClosedAllDay: true, is24Hours: false },
  ];

  // Helper function to check if closing time is after opening time
  const isValidBusinessHours = (openingTime: string, closingTime: string): boolean => {
    if (!openingTime || !closingTime) return true; // Skip validation if either time is not set
    
    // Convert HH:MM to minutes for easy comparison
    const [openHour, openMinute] = openingTime.split(':').map(Number);
    const [closeHour, closeMinute] = closingTime.split(':').map(Number);
    
    const openingMinutes = openHour * 60 + openMinute;
    const closingMinutes = closeHour * 60 + closeMinute;
    
    // Both overnight operations (e.g., 22:00-02:00) and normal operations (e.g., 09:00-17:00) are valid
    // The only invalid case is when opening and closing times are exactly the same
    return openingMinutes !== closingMinutes;
  };

  // Function to fetch opening hours from the database
  useEffect(() => {
    const fetchOpeningHours = async () => {
      if (!businessId) {
        // Initialize with default values if no business is selected
        setDailyOpeningHours(defaultOpeningHours);
        setOriginalHours(defaultOpeningHours);
        return;
      }

      setLoadingOpeningHours(true);
      try {
        const { data, error } = await supabaseClient
          .from('business_opening_hours')
          .select('business_id, created_at, day_of_week, id, opening_time, closing_time, is_closed_all_day, is_24_hours, updated_at')
          .eq('business_id', businessId);

        if (error) {
          throw error;
        }

        // Ensure all days are present, using fetched data or defaults
        const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        const fetchedHours: DailyOpeningHours[] = daysOfWeek.map(dayName => {
          const foundDay = data?.find(item => item.day_of_week === dayName);
          
          // Convert display times: 23:59 back to 00:00 for midnight representation
          const openingTime = foundDay?.opening_time ? foundDay.opening_time.substring(0, 5) : '';
          let closingTime = foundDay?.closing_time ? foundDay.closing_time.substring(0, 5) : '';
          
          // Convert 23:59 back to 00:00 for midnight display
          if (closingTime === '23:59') {
            closingTime = '00:00';
          }
          
          return {
            day: dayName,
            openingTime: foundDay?.is_24_hours ? '' : openingTime,
            closingTime: foundDay?.is_24_hours ? '' : closingTime,
            isClosedAllDay: foundDay?.is_closed_all_day || false,
            is24Hours: foundDay?.is_24_hours || false,
          };
        });
        setDailyOpeningHours(fetchedHours);
        setOriginalHours(fetchedHours);
      } catch (error: unknown) {
        console.error('Error fetching opening hours:', error);
        toast.error('Failed to fetch opening hours', { description: error instanceof Error ? error.message : String(error) });
        // Initialize with default values on error
        setDailyOpeningHours(defaultOpeningHours);
        setOriginalHours(defaultOpeningHours);
      } finally {
        setLoadingOpeningHours(false);
      }
    };

    fetchOpeningHours();
  }, [businessId]); // Fetch when businessId changes

  // Function to save opening hours to the database
  const handleSaveOpeningHours = async () => {
    if (!businessId) {
      toast.error("Please select a business first.");
      return;
    }

    const errors: { [key: number]: { openingTime?: boolean; closingTime?: boolean; timeOrder?: boolean } } = {};
    let isValid = true;

    dailyOpeningHours.forEach((day, index) => {
      if (!day.isClosedAllDay && !day.is24Hours) {
        if (!day.openingTime) {
          errors[index] = { ...errors[index], openingTime: true };
          isValid = false;
        }
        if (!day.closingTime) {
          errors[index] = { ...errors[index], closingTime: true };
          isValid = false;
        }
        
        // Check if closing time is after opening time
        if (day.openingTime && day.closingTime && !isValidBusinessHours(day.openingTime, day.closingTime)) {
          errors[index] = { ...errors[index], timeOrder: true };
          isValid = false;
        }
      }
    });

    setValidationErrors(errors);

    if (!isValid) {
      // Determine the appropriate error message
      const hasTimeOrderError = Object.values(errors).some(error => error.timeOrder);
      const hasMissingTimeError = Object.values(errors).some(error => error.openingTime || error.closingTime);
      
      if (hasTimeOrderError) {
        toast.error("Opening and closing times cannot be the same.");
      } else if (hasMissingTimeError) {
        toast.error("Please fill in both opening and closing hours for days that are not closed.");
      }
      return;
    }

    setLoadingOpeningHours(true);
    try {
      const updates = dailyOpeningHours.map(day => {
        // Convert 00:00 back to 23:59 for database storage (midnight representation)
        let closingTime = day.closingTime;
        if (closingTime === '00:00') {
          closingTime = '23:59';
        }
        
        return {
          business_id: businessId,
          day_of_week: day.day,
          opening_time: day.isClosedAllDay || day.is24Hours ? null : (day.openingTime || null), // Save empty string as null
          closing_time: day.isClosedAllDay || day.is24Hours ? null : (closingTime || null), // Save empty string as null
          is_closed_all_day: day.isClosedAllDay, // Include the new property
          is_24_hours: day.is24Hours, // Include the 24-hour flag
        };
      });
      
      // Use upsert to insert new rows or update existing ones based on business_id and day_of_week
      const { error } = await supabaseClient
        .from('business_opening_hours')
        .upsert(updates, { onConflict: 'business_id, day_of_week' });

      if (error) {
        throw error;
      }

      toast.success('Opening hours saved successfully.');
      setIsEditing(false);
      setOriginalHours([...dailyOpeningHours]); // Update original hours after successful save
    } catch (error: unknown) {
      console.error('Error saving opening hours:', error);
      toast.error('Failed to save opening hours', { description: error instanceof Error ? error.message : String(error) });
    } finally {
      setLoadingOpeningHours(false);
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setDailyOpeningHours([...originalHours]); // Revert to original values
    setValidationErrors({}); // Clear any validation errors
  };

  // Helper function to split HH:mm time string into hour and minute
  const splitTime = (time: string | null) => {
    if (!time) return { hour: '', minute: '' };
    const [hour, minute] = time.split(':');
    return { hour: hour || '', minute: minute || '' };
  };

  // Helper function to combine hour and minute into HH:mm time string
  const combineTime = (hour: string, minute: string) => {
    if (!hour && !minute) return ''; // Return empty string if both are empty
    if (!hour) return `00:${minute}`; // Default hour to 00 if only minute is selected
    if (!minute) return `${hour}:00`; // Default minute to 00 if only hour is selected
    return `${hour}:${minute}`;
  };

  // Update a specific day's hours
  const updateDayHours = (index: number, field: keyof DailyOpeningHours, value: unknown) => {
    const newHours = [...dailyOpeningHours];
    newHours[index] = { ...newHours[index], [field]: value };
    
    // If closed all day is checked, clear the times and disable 24 hours
    if (field === 'isClosedAllDay' && value === true) {
      newHours[index].openingTime = '';
      newHours[index].closingTime = '';
      newHours[index].is24Hours = false;
    }
    
    // If 24 hours is checked, clear the times and disable closed all day
    if (field === 'is24Hours' && value === true) {
      newHours[index].openingTime = '';
      newHours[index].closingTime = '';
      newHours[index].isClosedAllDay = false;
    }
    
    setDailyOpeningHours(newHours);
  };

  return (
    <SettingsCard
      icon={Clock}
      title="Business Opening Hours"
      description="Set your business hours for each day. Overnight operations supported."
    >
        <div className="grid grid-cols-7 row-gap-4">
          {/* Header Row */}
          <div className={`font-semibold text-sm mb-2 ${!isEditing ? "text-muted-foreground" : "text-foreground"}`}>Day</div>
          <div className={`text-center font-semibold text-sm col-span-2 mb-2 ${!isEditing ? "text-muted-foreground" : "text-foreground"}`}>Opening</div>
          <div className={`text-center font-semibold text-sm col-span-2 mb-2 ${!isEditing ? "text-muted-foreground" : "text-foreground"}`}>Closing</div>
          <div className={`text-center font-semibold text-sm mb-2 ${!isEditing ? "text-muted-foreground" : "text-foreground"}`}>Open 24 Hours</div>
          <div className={`text-center font-semibold text-sm mb-2 ${!isEditing ? "text-muted-foreground" : "text-foreground"}`}>Closed All Day</div>

          {/* Hour/Minute Labels Row */}
          <div></div>
          <div className={`flex justify-center text-xs mb-2 col-span-2 ${!isEditing ? "text-muted-foreground" : "text-muted-foreground"}`}>
            <div className="w-full text-center">Hour</div>
            <div className="w-full text-center">Minute</div>
          </div>
          <div className={`flex text-xs mb-2 col-span-2 ${!isEditing ? "text-muted-foreground" : "text-muted-foreground"}`}>
            <div className="w-full text-center">Hour</div>
            <div className="w-full text-center">Minute</div>
          </div>
          <div></div>
          <div></div>

          {/* Days of week rows */}
          {dailyOpeningHours.map((dayHours, index) => {
            const { hour: openingHour, minute: openingMinute } = splitTime(dayHours.openingTime);
            const { hour: closingHour, minute: closingMinute } = splitTime(dayHours.closingTime);

            return (
              <React.Fragment key={index}>
                {/* Day label */}
                <div className="flex items-center py-2 border-t border-border">
                  <Label className={`text-sm ${!isEditing ? "text-muted-foreground" : "text-foreground"}`}>{dayHours.day}</Label>
                </div>

                {/* Opening time selectors */}
                <div className="col-span-2 flex gap-2 justify-center items-center py-2 border-t border-border px-3">
                  <Select
                    value={openingHour}
                    onValueChange={(value) => {
                      updateDayHours(index, 'openingTime', combineTime(value, openingMinute));
                    }}
                    disabled={!isEditing || dayHours.isClosedAllDay || dayHours.is24Hours}
                  >
                    <SelectTrigger className={` h-8 text-sm ${validationErrors[index]?.openingTime || validationErrors[index]?.timeOrder ? '!outline !outline-red-500' : ''}`}>
                      <SelectValue placeholder="HH" />
                    </SelectTrigger>
                    <SelectContent className="">
                      {hourOptions.map(hour => (
                        <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={openingMinute}
                    onValueChange={(value) => {
                      updateDayHours(index, 'openingTime', combineTime(openingHour, value));
                    }}
                    disabled={!isEditing || dayHours.isClosedAllDay || dayHours.is24Hours}
                  >
                    <SelectTrigger className={` h-8 text-sm ${validationErrors[index]?.openingTime || validationErrors[index]?.timeOrder ? '!outline !outline-red-500' : ''}`}>
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent className="">
                      {minuteOptions.map(minute => (
                        <SelectItem key={minute} value={minute}>{minute}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Closing time selectors */}
                <div className="col-span-2 flex gap-2 justify-center items-center py-2 border-t border-border px-3">
                  <Select
                    value={closingHour}
                    onValueChange={(value) => {
                      updateDayHours(index, 'closingTime', combineTime(value, closingMinute));
                    }}
                    disabled={!isEditing || dayHours.isClosedAllDay || dayHours.is24Hours}
                  >
                    <SelectTrigger className={` h-8 text-sm ${validationErrors[index]?.closingTime || validationErrors[index]?.timeOrder ? '!outline !outline-red-500' : ''}`}>
                      <SelectValue placeholder="HH" />
                    </SelectTrigger>
                    <SelectContent className="">
                      {hourOptions.map(hour => (
                        <SelectItem key={hour} value={hour}>{hour}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={closingMinute}
                    onValueChange={(value) => {
                      updateDayHours(index, 'closingTime', combineTime(closingHour, value));
                    }}
                    disabled={!isEditing || dayHours.isClosedAllDay || dayHours.is24Hours}
                  >
                    <SelectTrigger className={` h-8 text-sm ${validationErrors[index]?.closingTime || validationErrors[index]?.timeOrder ? '!outline !outline-red-500' : ''}`}>
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent className="">
                      {minuteOptions.map(minute => (
                        <SelectItem key={minute} value={minute}>{minute}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Overnight indicator */}
                  {dayHours.openingTime && dayHours.closingTime && !dayHours.isClosedAllDay && !dayHours.is24Hours && (() => {
                    const [openHour, openMinute] = dayHours.openingTime.split(':').map(Number);
                    const [closeHour, closeMinute] = dayHours.closingTime.split(':').map(Number);
                    const openingMinutes = openHour * 60 + openMinute;
                    const closingMinutes = closeHour * 60 + closeMinute;
                    return closingMinutes < openingMinutes;
                  })() && (
                    <span className="text-xs text-blue-600 font-medium ml-1">
                      +1 day
                    </span>
                  )}
                </div>

                {/* 24 hours checkbox */}
                <div className="flex justify-center items-center py-2 border-t border-border">
                  <Checkbox
                    checked={dayHours.is24Hours}
                    onCheckedChange={(checked) => {
                      updateDayHours(index, 'is24Hours', checked as boolean);
                    }}
                    disabled={!isEditing || dayHours.isClosedAllDay}
                    className=""
                  />
                </div>

                {/* Closed all day checkbox */}
                <div className="flex justify-center items-center py-2 border-t border-border">
                  <Checkbox
                    checked={dayHours.isClosedAllDay}
                    onCheckedChange={(checked) => {
                      updateDayHours(index, 'isClosedAllDay', checked as boolean);
                    }}
                    disabled={!isEditing || dayHours.is24Hours}
                    className=""
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
          
        <div className="pt-4">
          {!isEditing ? (
            <Button 
              type="button" 
              onClick={handleStartEditing} 
              className="w-full"
              variant="outline"
            >
              Start Editing Opening Hours
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button 
                type="button" 
                onClick={handleCancelEditing} 
                variant="outline" 
                className="flex-1"
              >
                Cancel Edits
              </Button>
              <Button 
                type="button" 
                onClick={handleSaveOpeningHours}
                disabled={loadingOpeningHours} 
                className="flex-1"
              >
                {loadingOpeningHours ? 'Saving...' : 'Save Opening Hours'}
              </Button>
            </div>
          )}
        </div>
    </SettingsCard>
  );
};

export default BusinessOpeningHoursManager;